import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Card,
  CardBody,
  Grid,
  GridItem,
  Icon,
  Divider,
  useToast,
  Stat,
  StatLabel,
  StatNumber,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Flex,
  Avatar,
  Tooltip,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  SimpleGrid,
  Input,
  Select,
  InputGroup,
  InputRightElement,
  Code,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Collapse,
  FormControl,
  FormLabel,
  Textarea,
  Spinner,
} from "@chakra-ui/react";
import {
  FiArrowLeft,
  FiMail,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiActivity,
  FiSearch,
  FiServer,
  FiCode,
  FiEdit2,
  FiLock,
} from "react-icons/fi";
import { getData, updateData } from "../../redux/actions/common.action";
import { BASE_URL } from "../../config/RequestMethod";
import { useDispatch, useSelector } from "react-redux";

// Helper to format currency
const formatCurrency = (amount: any, currency: string) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(Number(amount) || 0);
};

// Helper: Normalize Data for UI
const mapPaymentDetails = (data: any, source: string) => {
  if (!data) return null;

  let mapped = {
    id: "",
    status: "",
    amount: "",
    currency: "",
    date: "",
    email: "",
    method: "",
    description: "",
    raw: data,
  };

  if (source === "RAZORPAY") {
    if (data.entity === "order") {
      mapped.id = data.id;
      mapped.status = data.status;
      mapped.amount = (data.amount / 100).toString();
      mapped.currency = data.currency;
      mapped.date = new Date(data.created_at * 1000).toLocaleString();
      const payment = data.related_payments?.items?.[0];
      if (payment) {
        mapped.email = payment.email;
        mapped.method = payment.method;
        mapped.description = payment.description;
      }
    } else if (data.entity === "payment") {
      mapped.id = data.id;
      mapped.status = data.status;
      mapped.amount = (data.amount / 100).toString();
      mapped.currency = data.currency;
      mapped.date = new Date(data.created_at * 1000).toLocaleString();
      mapped.email = data.email;
      mapped.method = data.method;
    }
  } else if (source === "PAYPAL") {
    mapped.id = data.id;
    mapped.status = data.status;
    const purchaseUnit = data.purchase_units?.[0];
    const capture = purchaseUnit?.payments?.captures?.[0];

    if (capture) {
      mapped.amount = capture.amount?.value;
      mapped.currency = capture.amount?.currency_code;
    } else if (purchaseUnit?.amount) {
      mapped.amount = purchaseUnit.amount.value;
      mapped.currency = purchaseUnit.amount.currency_code;
    }

    mapped.date = new Date(data.create_time).toLocaleString();
    mapped.email = data.payer?.email_address;
    mapped.method = "PayPal";
    mapped.description = purchaseUnit?.description;
  }

  return mapped;
};

const PaymentDetails = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  // Local proposal state
  const [proposal, setProposal] = useState(state?.proposal || null);

  const dispatch: any = useDispatch();
  const toast = useToast();

  const { searchedPaymentDetails, loading, error } = useSelector(
    (state: any) => state.common
  );

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isPaymentOpen,
    onOpen: onPaymentOpen,
    onClose: onPaymentClose,
  } = useDisclosure();
  const [selectedEmi, setSelectedEmi] = useState<any>(null);
  const [isSending, setIsSending] = useState(false);
  const [showRawJson, setShowRawJson] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentRemark, setPaymentRemark] = useState("");

  // Payment Mode State
  const [isUpdatingMode, setIsUpdatingMode] = useState(false);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState<string>(
    state?.proposal?.paymentMode || "MANUAL"
  );

  // Search State
  const [searchId, setSearchId] = useState("");
  const [searchProvider, setSearchProvider] = useState("PAYPAL");

  // Re-fetch proposal on mount (optional)
  useEffect(() => {
    if (proposal?._id) {
      // dispatch(getData(...)) if you have a specific endpoint
    }
  }, [proposal?._id]);

  // If navigated without proposal
  if (!proposal) {
    return (
      <Container centerContent p={10}>
        <Text>No proposal data found. Please navigate from the list page.</Text>
        <Button mt={4} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Container>
    );
  }

  const totalAmount = proposal.total || 0;
  const totalPaid =
    proposal.emiSteps
      ?.filter((step: any) => step.isPaid)
      ?.reduce((sum: number, step: any) => sum + step.pay, 0) || 0;
  const totalPending = totalAmount - totalPaid;
  const progressPercent = totalAmount > 0 ? (totalPaid / totalAmount) * 100 : 0;

  // --- CHECK LOCK CONDITION ---
  const isPaymentStarted = proposal.emiSteps?.some((step: any) => step.isPaid);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSendReminder = async () => {
    if (!selectedEmi) return;
    setIsSending(true);
    try {
      await dispatch(
        updateData(
          proposal._id,
          "proposal/send-manual-reminder",
          { emiStepId: selectedEmi._id },
          () => onClose(),
          toast
        )
      );
    } catch (error) {
      console.error("Error sending reminder:", error);
    } finally {
      setIsSending(false);
    }
  };

  // --- Handle Manual Payment ---
  const handleOpenPaymentModal = (emi: any) => {
    setSelectedEmi(emi);
    setPaymentDate("");
    setPaymentRemark("");
    onPaymentOpen();
  };

  const handleUpdateManualPayment = async () => {
    if (!paymentDate) {
      toast({
        title: "Payment Date Required",
        description: "Please select the payment date and time.",
        status: "warning",
        position: "top-right",
        isClosable: true,
      });
      return;
    }

    if (!paymentRemark.trim()) {
      toast({
        title: "Remark Required",
        description: "Please provide a remark for this payment.",
        status: "warning",
        position: "top-right",
        isClosable: true,
      });
      return;
    }

    setIsProcessing(true);
    const payload = {
      emiId: selectedEmi._id,
      paymentDate: new Date(paymentDate).toISOString(),
      remark: paymentRemark,
    };

    try {
      const token = Cookies.get("token");

      // 1. Call API to update the manual payment
      const updateRes = await axios.put(
        `${BASE_URL}proposal/update-manual-payment/${proposal._id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (updateRes.data) {
        toast({
          title: updateRes.data.message || "Manual payment updated successfully.",
          status: "success",
          position: "top-right",
          isClosable: true,
          duration: 4000,
        });

        // 2. Fetch fresh proposal to refresh UI
        try {
          const freshRes = await axios.get(
            `${BASE_URL}proposal/${proposal._id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (freshRes.data) {
            setProposal(freshRes.data);
          }
        } catch (fetchErr) {
          // fallback: use the returned data from the update
          if (updateRes.data.Data) setProposal(updateRes.data.Data);
        }

        onPaymentClose();
        setSelectedEmi(null);
        setPaymentDate("");
        setPaymentRemark("");
      }
    } catch (error: any) {
      toast({
        title: error?.response?.data?.message || "Failed to update manual payment.",
        status: "error",
        position: "top-right",
        isClosable: true,
        duration: 5000,
      });
      console.error("Error updating manual payment:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // New: only change local selectedPaymentMode on select
  const handlePaymentModeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPaymentMode(e.target.value);
  };

  // New: explicit button to update mode via API
  const handlePaymentModeUpdate = async () => {
    if (isPaymentStarted) return;

    if (selectedPaymentMode === proposal.paymentMode) {
      toast({
        title: "No changes to update",
        status: "info",
        position: "top-right",
        isClosable: true,
      });
      return;
    }

    setIsUpdatingMode(true);
    try {
      const res: any = await dispatch(
        updateData(
          proposal._id,
          "proposal/update-payment-mode",
          { paymentMode: selectedPaymentMode },
          null,
          toast
        )
      );

      // Adjust this based on your API response structure
      // If your backend returns { data: updatedProposal, message: "..."}:
      const updatedProposal = res?.Data || res; // <<< ADJUST THIS LINE IF NEEDED

      if (updatedProposal) {
        setProposal(updatedProposal);
      }

      toast({
        title: "Payment mode updated",
        status: "success",
        position: "top-right",
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsUpdatingMode(false);
    }
  };

  const openReminderModal = (emi: any) => {
    setSelectedEmi(emi);
    onOpen();
  };

  const handlePaymentSearch = () => {
    if (!searchId) {
      toast({
        title: "Input Required",
        description: "Please enter an Order ID or Transaction ID",
        status: "warning",
      });
      return;
    }
    dispatch(
      getData(`payment/search?id=${searchId.trim()}&provider=${searchProvider}`)
    );
  };

  const displayData = searchedPaymentDetails
    ? mapPaymentDetails(
        searchedPaymentDetails.details,
        searchedPaymentDetails.source
      )
    : null;

  return (
    <Box minH="100vh" bg="gray.50" p={{ base: 2, md: 4 }}>
      <Container maxW="100%" px={{ base: 2, md: 8 }}>
        {/* Header */}
        <Flex
          mb={6}
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "start", md: "center" }}
          gap={4}
          bg="white"
          p={4}
          borderRadius="xl"
          boxShadow="sm"
        >
          <HStack>
            <Button
              leftIcon={<FiArrowLeft />}
              variant="ghost"
              onClick={() => navigate(-1)}
              size="sm"
            >
              Back
            </Button>
            <Heading size={{ base: "md", md: "lg" }} color="gray.700">
              Payment Details: {proposal.proposalName}
            </Heading>
          </HStack>
          <Badge
            colorScheme={proposal.status === "ACCEPTED" ? "green" : "orange"}
            fontSize="md"
            px={3}
            py={1}
            borderRadius="md"
          >
            {proposal.status}
          </Badge>
        </Flex>

        <Tabs variant="enclosed" colorScheme="blue" isLazy>
          <TabList mb={4}>
            <Tab fontWeight="600">
              <Icon as={FiActivity} mr={2} /> Overview & History
            </Tab>
            <Tab fontWeight="600">
              <Icon as={FiServer} mr={2} /> Live Status Check
            </Tab>
          </TabList>

          <TabPanels>
            {/* TAB 1: OVERVIEW & HISTORY */}
            <TabPanel p={0}>
              <Grid
                templateColumns={{ base: "1fr", lg: "3fr 1fr" }}
                gap={6}
                mb={8}
              >
                {/* Left Col: Stats & History */}
                <GridItem>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
                    <Card
                      borderRadius="xl"
                      boxShadow="sm"
                      borderLeft="4px solid"
                      borderColor="blue.500"
                    >
                      <CardBody>
                        <Stat>
                          <StatLabel color="gray.500">Total Contract</StatLabel>
                          <StatNumber color="blue.600">
                            {proposal?.currency} {totalAmount?.toLocaleString()}
                          </StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card
                      borderRadius="xl"
                      boxShadow="sm"
                      borderLeft="4px solid"
                      borderColor="green.500"
                    >
                      <CardBody>
                        <Stat>
                          <StatLabel color="gray.500">Total Received</StatLabel>
                          <StatNumber color="green.500">
                            {proposal.currency} {totalPaid.toLocaleString()}
                          </StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                    <Card
                      borderRadius="xl"
                      boxShadow="sm"
                      borderLeft="4px solid"
                      borderColor="orange.500"
                    >
                      <CardBody>
                        <Stat>
                          <StatLabel color="gray.500">Pending</StatLabel>
                          <StatNumber color="orange.500">
                            {proposal.currency} {totalPending.toLocaleString()}
                          </StatNumber>
                        </Stat>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  {/* Transaction Accordion */}
                  <Card borderRadius="xl" boxShadow="sm" overflow="hidden">
                    <Box
                      p={4}
                      bg="gray.100"
                      borderBottom="1px solid"
                      borderColor="gray.200"
                    >
                      <Heading size="md" color="gray.700">
                        Detailed Transaction Log
                      </Heading>
                    </Box>
                    <Accordion allowToggle defaultIndex={[]} bg="white">
                      {proposal.emiSteps?.map((step: any, index: number) => (
                        <AccordionItem
                          key={step._id}
                          borderBottom="1px solid"
                          borderColor="gray.100"
                        >
                          {({ isExpanded }) => (
                            <>
                              <h2>
                                <AccordionButton
                                  _expanded={{
                                    bg: step.isPaid ? "green.50" : "orange.50",
                                  }}
                                  py={4}
                                >
                                  <Flex
                                    w="full"
                                    align="center"
                                    justify="space-between"
                                    gap={2}
                                  >
                                    <HStack spacing={4}>
                                      <Box
                                        p={2}
                                        borderRadius="full"
                                        bg={
                                          step.isPaid
                                            ? "green.100"
                                            : "orange.100"
                                        }
                                        color={
                                          step.isPaid
                                            ? "green.600"
                                            : "orange.600"
                                        }
                                      >
                                        <Icon
                                          as={
                                            step.isPaid
                                              ? FiCheckCircle
                                              : FiClock
                                          }
                                          boxSize={5}
                                        />
                                      </Box>
                                      <VStack align="start" spacing={0}>
                                        <Text fontWeight="bold" fontSize="md">
                                          {step.label}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                          Due: {formatDate(step.emiPaymentDate)}
                                        </Text>
                                      </VStack>
                                    </HStack>
                                    <HStack>
                                      <Text fontWeight="bold">
                                        {proposal.currency}{" "}
                                        {step.pay.toLocaleString()}
                                      </Text>
                                      <Badge
                                        colorScheme={
                                          step.isPaid ? "green" : "red"
                                        }
                                      >
                                        {step.isPaid ? "PAID" : "PENDING"}
                                      </Badge>
                                      <AccordionIcon />
                                    </HStack>
                                  </Flex>
                                </AccordionButton>
                              </h2>
                              <AccordionPanel
                                pb={6}
                                bg={
                                  isExpanded
                                    ? step.isPaid
                                      ? "green.50"
                                      : "orange.50"
                                    : "white"
                                }
                              >
                                {step.isPaid ? (
                                  <Card variant="outline" bg="white" mt={2}>
                                    <CardBody>
                                      <Grid
                                        templateColumns={{
                                          base: "1fr",
                                          md: "repeat(2, 1fr)",
                                          lg: "repeat(4, 1fr)",
                                        }}
                                        gap={6}
                                      >
                                        <GridItem>
                                          <Text fontSize="xs" color="gray.500">
                                            TRANSACTION ID
                                          </Text>
                                          <Text
                                            fontWeight="600"
                                            fontFamily="monospace"
                                          >
                                            {step.transactionData
                                              ?.transactionId || "N/A"}
                                          </Text>
                                        </GridItem>
                                        <GridItem>
                                          <Text fontSize="xs" color="gray.500">
                                            ORDER ID
                                          </Text>
                                          <Text
                                            fontWeight="600"
                                            fontFamily="monospace"
                                          >
                                            {step.transactionData?.orderId ||
                                              "N/A"}
                                          </Text>
                                        </GridItem>
                                        <GridItem>
                                          <Text fontSize="xs" color="gray.500">
                                            SOURCE
                                          </Text>
                                          <Badge>
                                            {step.transactionData
                                              ?.paymentSource || "MANUAL"}
                                          </Badge>
                                        </GridItem>
                                        <GridItem>
                                          <Text fontSize="xs" color="gray.500">
                                            PAID DATE
                                          </Text>
                                          <Text fontWeight="600">
                                            {formatDateTime(
                                              step.emiPaymentReceivedDate
                                            )}
                                          </Text>
                                        </GridItem>
                                      </Grid>
                                    </CardBody>
                                  </Card>
                                ) : (
                                  <Flex
                                    justify="space-between"
                                    align="center"
                                    mt={2}
                                    gap={2}
                                  >
                                    <Text fontSize="sm">Payment pending.</Text>
                                    <HStack>
                                      {proposal.paymentMode === "MANUAL" && (
                                        <Button
                                          size="sm"
                                          leftIcon={<FiEdit2 />}
                                          colorScheme="green"
                                          onClick={() => handleOpenPaymentModal(step)}
                                        >
                                          Update
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        leftIcon={<FiMail />}
                                        colorScheme="blue"
                                        onClick={() => openReminderModal(step)}
                                      >
                                        Send Reminder
                                      </Button>
                                    </HStack>
                                  </Flex>
                                )}
                              </AccordionPanel>
                            </>
                          )}
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </Card>
                </GridItem>

                {/* Right Col: Info & CONFIG */}
                <GridItem>
                  <VStack spacing={6}>
                    {/* --- PAYMENT CONFIGURATION CARD --- */}
                    <Card
                      w="full"
                      borderRadius="xl"
                      boxShadow="sm"
                      bg="blue.50"
                      border="1px solid"
                      borderColor="blue.100"
                    >
                      <CardBody>
                        <Heading size="sm" mb={4} color="blue.700">
                          Payment Config
                        </Heading>
                        <VStack align="stretch" spacing={4}>
                          {/* Payment Mode Selector with Lock Logic + Update Button */}
                          <Box>
                            <HStack justify="space-between" mb={1}>
                              <Text fontSize="sm" color="gray.600">
                                Mode
                              </Text>
                              {isPaymentStarted && (
                                <Tooltip label="Cannot change mode after payments have started">
                                  <span>
                                    <Icon as={FiLock} color="red.500" />
                                  </span>
                                </Tooltip>
                              )}
                            </HStack>
                            <HStack spacing={2}>
                              <Select
                                bg="white"
                                size="sm"
                                borderRadius="md"
                                value={selectedPaymentMode}
                                onChange={handlePaymentModeSelect}
                                isDisabled={isPaymentStarted || isUpdatingMode}
                                icon={
                                  isPaymentStarted ? <FiLock /> : <FiEdit2 />
                                }
                              >
                                <option value="MANUAL">Manual (Offline)</option>
                                <option value="AUTO_PAYPAL">Auto PayPal</option>
                                <option value="AUTO_RAZORPAY">
                                  Auto Razorpay
                                </option>
                              </Select>
                              <Button
                                size="sm"
                                colorScheme="blue"
                                onClick={handlePaymentModeUpdate}
                                isDisabled={
                                  isPaymentStarted ||
                                  isUpdatingMode ||
                                  selectedPaymentMode === proposal.paymentMode
                                }
                                isLoading={isUpdatingMode}
                              >
                                Update
                              </Button>
                            </HStack>
                            {isPaymentStarted && (
                              <Text fontSize="10px" color="red.500" mt={1}>
                                Locked: Payment plan is active.
                              </Text>
                            )}
                          </Box>

                          <HStack justify="space-between">
                            <Text fontSize="sm" color="gray.600">
                              Type
                            </Text>
                            <Text fontWeight="600" fontSize="sm">
                              {proposal.emiSteps?.length > 1
                                ? "EMI Schedule"
                                : "Full Payment"}
                            </Text>
                          </HStack>

                          <Divider borderColor="blue.200" />

                          <Box>
                            <HStack justify="space-between" mb={2}>
                              <Text
                                fontSize="sm"
                                color="gray.600"
                                fontWeight="bold"
                              >
                                Completion
                              </Text>
                              <Text
                                fontWeight="700"
                                fontSize="sm"
                                color="blue.700"
                              >
                                {progressPercent.toFixed(0)}%
                              </Text>
                            </HStack>
                            <Box
                              w="100%"
                              bg="white"
                              h="3"
                              borderRadius="full"
                              overflow="hidden"
                              border="1px solid"
                              borderColor="blue.100"
                            >
                              <Box
                                w={`${progressPercent}%`}
                                h="100%"
                                bgGradient="linear(to-r, blue.400, blue.600)"
                                transition="width 0.5s ease-in-out"
                              />
                            </Box>
                          </Box>
                        </VStack>
                      </CardBody>
                    </Card>

                    {/* Client Info Card */}
                    <Card w="full" borderRadius="xl" boxShadow="sm" bg="white">
                      <CardBody>
                        <Heading size="sm" mb={4} color="gray.700">
                          Client Info
                        </Heading>
                        <VStack align="stretch" spacing={3}>
                          <HStack>
                            <Avatar size="sm" name={proposal.name} />
                            <Box>
                              <Text fontWeight="600" fontSize="sm">
                                {proposal.name}
                              </Text>
                              <Text fontSize="xs">{proposal.email}</Text>
                            </Box>
                          </HStack>
                          <HStack>
                            <Icon as={FiCreditCard} color="gray.400" />
                            <Text fontSize="sm">{proposal.phone}</Text>
                          </HStack>
                        </VStack>
                      </CardBody>
                    </Card>
                  </VStack>
                </GridItem>
              </Grid>
            </TabPanel>

            {/* TAB 2: LIVE STATUS CHECK (SEARCH) */}
            <TabPanel p={0}>
              <Card borderRadius="xl" boxShadow="sm" mb={6}>
                <CardBody>
                  <Heading size="md" mb={2}>
                    Live Payment Lookup
                  </Heading>
                  <Flex gap={4} direction={{ base: "column", md: "row" }}>
                    <Select
                      w={{ base: "100%", md: "200px" }}
                      value={searchProvider}
                      onChange={(e) => setSearchProvider(e.target.value)}
                    >
                      <option value="PAYPAL">PayPal</option>
                      <option value="RAZORPAY">Razorpay</option>
                    </Select>
                    <InputGroup flex={1}>
                      <Input
                        placeholder="Enter Order ID or Transaction ID..."
                        value={searchId}
                        onChange={(e) => setSearchId(e.target.value)}
                      />
                      <InputRightElement width="4.5rem">
                        <Button
                          h="1.75rem"
                          size="sm"
                          onClick={handlePaymentSearch}
                          isLoading={loading}
                        >
                          <Icon as={FiSearch} />
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </Flex>
                </CardBody>
              </Card>

              {/* SUCCESS RESULTS DISPLAY */}
              {!loading && !error && displayData && (
                <Card
                  borderRadius="xl"
                  boxShadow="md"
                  borderTop="4px solid"
                  borderColor={
                    displayData.status.toUpperCase() === "COMPLETED" ||
                    displayData.status === "paid" ||
                    displayData.status === "captured"
                      ? "green.500"
                      : "orange.500"
                  }
                  bg="white"
                >
                  <CardBody>
                    <HStack mb={4} justify="space-between" align="start">
                      <HStack>
                        <Icon
                          as={FiCheckCircle}
                          color="green.500"
                          boxSize={6}
                        />
                        <VStack align="start" spacing={0}>
                          <Heading size="md" color="gray.800">
                            Payment Found
                          </Heading>
                          <Text fontSize="xs" color="gray.500">
                            Source: {searchedPaymentDetails.source}
                          </Text>
                        </VStack>
                      </HStack>
                      <Badge colorScheme="green">
                        {displayData.status.toUpperCase()}
                      </Badge>
                    </HStack>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
                      <Box bg="gray.50" p={4} borderRadius="md">
                        <Text fontSize="xs" color="gray.500">
                          AMOUNT
                        </Text>
                        <Text fontWeight="bold">
                          {formatCurrency(
                            displayData.amount,
                            displayData.currency
                          )}
                        </Text>
                      </Box>
                      {/* Add other fields similarly */}
                    </SimpleGrid>

                    {/* View Raw Data Toggle */}
                    <Box>
                      <Button
                        size="xs"
                        variant="ghost"
                        leftIcon={<FiCode />}
                        onClick={() => setShowRawJson(!showRawJson)}
                        mb={2}
                      >
                        {showRawJson
                          ? "Hide Raw API Response"
                          : "View Raw API Response"}
                      </Button>
                      <Collapse in={showRawJson}>
                        <Box
                          bg="gray.50"
                          color="green.300"
                          p={4}
                          borderRadius="md"
                          fontSize="xs"
                          fontFamily="monospace"
                          maxH="300px"
                          overflowY="auto"
                        >
                          <Code
                            variant="subtle"
                            colorScheme="blackAlpha"
                            bg="transparent"
                            display="block"
                            whiteSpace="pre"
                          >
                            {JSON.stringify(displayData.raw, null, 2)}
                          </Code>
                        </Box>
                      </Collapse>
                    </Box>
                  </CardBody>
                </Card>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>

      {/* Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Send Reminder</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Send manual reminder for <strong>{selectedEmi?.label}</strong>?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSendReminder}
              isLoading={isSending}
            >
              Send
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Manual Payment Update Modal */}
      <Modal isOpen={isPaymentOpen} onClose={onPaymentClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Manual Payment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Text fontSize="sm" w="full">
                Mark <strong>{selectedEmi?.label}</strong> as Paid manually.
              </Text>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold">
                  Payment Date
                </FormLabel>
                <Input
                  type="datetime-local"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="bold">
                  Payment Remark / Ref Number
                </FormLabel>
                <Textarea
                  placeholder="E.g., Cash, Cheque Ref 1234, Bank Transfer ID"
                  value={paymentRemark}
                  onChange={(e) => setPaymentRemark(e.target.value)}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPaymentClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdateManualPayment}
              isLoading={isProcessing}
            >
              Update Payment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PaymentDetails;
